import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleLang = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-sm border-b border-white/5 transition-all duration-300">
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-24">
          <Link
            to="/"
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setMobileOpen(false)}
          >
            <img
              alt="The Screen Squad — Screen Repair Service"
              className="h-14 w-auto object-contain"
              src="https://static.readdy.ai/image/e2264d4847cbc98dfbe6fd1d0e6b7ca4/c480cc975a256a5055b1791bd11fefde.png"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-bold tracking-wider whitespace-nowrap transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-primary-500'
                    : 'text-foreground-50 hover:text-primary-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="flex items-center gap-2 whitespace-nowrap bg-white/5 border border-white/10 hover:border-primary-500 hover:bg-primary-500/10 text-foreground-50 text-sm font-bold px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer"
              title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              <i className="ri-global-line text-lg w-5 h-5 flex items-center justify-center" />
              <span>{i18n.language === 'es' ? 'EN' : 'ES'}</span>
            </button>

            {/* Phone button — filled blue like Map Robot */}
            <a
              href="tel:+16574338533"
              className="hidden md:flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-base font-bold px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center" />
              {t('nav.phone')}
            </a>

            {/* Quote button — yellow accent like Map Robot secondary */}
            <Link
              to="/contact"
              className="hidden md:flex items-center gap-2 whitespace-nowrap bg-accent-500 hover:bg-accent-600 text-black text-base font-bold px-6 py-3 rounded-full transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-mail-send-line text-lg w-5 h-5 flex items-center justify-center" />
              {t('nav.quote')}
            </Link>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-50"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl w-6 h-6 flex items-center justify-center ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-white/10">
          <div className="px-4 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`text-lg font-bold tracking-wider py-2 ${
                  isActive(link.path) ? 'text-primary-500' : 'text-foreground-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:+16574338533"
              className="flex items-center gap-2 bg-primary-500 text-white font-bold py-3 px-5 rounded-full justify-center"
            >
              <i className="ri-phone-line text-lg" />
              {t('nav.phone')}
            </a>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 bg-accent-500 text-black font-bold px-5 py-3 rounded-full text-center justify-center"
            >
              <i className="ri-mail-send-line text-lg" />
              {t('nav.quote')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}